import { useMemo } from 'react';
import {
  Box,
  Card,
  Grid,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useCustomEventTracking } from '../../../../common/useCustomEventTracking';
import { useNavigationUtils } from '../../../../common/utils/useNavigationUtils';
import { getInternalPath } from '../../../../common/utils/urlUtils';
import { Badge } from '../../../../common/Badge';
import { BadgeType } from '../../../../common/enums';
import { PartnerProfileResourceType, PartnerResourceTranslation } from '../../../../common/models/PartnerProfile';
import { useSolutionOrg } from '../../../../config/useSolutionOrg';
import { usePartnerProfileConfig } from '../Enrollment/usePartnerProfileConfig';
import {
  getResourceBadgeText,
  getResourceBadgeStyle,
} from '../../../../common/utils/partnerResourceUtils';
import { useResourceDialog } from './useResourceDialog';
import { ResourcePreviewDialog } from './ResourcePreviewDialog';
import { getCanonicalPartnerName } from '../../../../common/constants';

type Props = {
  partnerName: string;
  partnerCode?: string;
};

export const PartnerRelatedArticlesSection = ({
  partnerName,
  partnerCode,
}: Props) => {
  const theme = useTheme();
  const { trackPartnerDetail } = useCustomEventTracking();
  const { t } = useTranslation();
  const { openExternalLink } = useNavigationUtils();
  const { baseUrl } = useSolutionOrg();
  const { partnerResources } = usePartnerProfileConfig();

  // Rename-stable name for deriving analytics event names (see getCanonicalPartnerName).
  const partnerKeyName = getCanonicalPartnerName(partnerCode, partnerName);

  const articles = useMemo(
    () => [...(partnerResources ?? [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    [partnerResources],
  );

  const {
    videoDialog,
    pdfDialog,
    openVideoDialog,
    openPdfDialog,
    closeVideoDialog,
    closePdfDialog,
  } = useResourceDialog();

  const renderCardContent = (article: PartnerResourceTranslation, cta: string, index: number) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box
        sx={{
          p: theme.spacing(2.5),
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(1.5),
          borderBottom: `${theme.spacing(0.125)} solid ${theme.palette.grey[200]}`,
          flexGrow: 1,
        }}
      >
        <Badge
          badgeType={BadgeType.Checkmark}
          badgeText={getResourceBadgeText(article.resourceType, t)}
          optionalStyles={{
            width: 'fit-content',
            ...getResourceBadgeStyle(article.resourceType, theme),
          }}
        />
        <Typography variant="h15" gutterBottom
          sx={{ flexGrow: 1 }}
        >
          {article.title}
        </Typography>
        <Typography
          variant="body8"
          color={theme.palette.grey[700]}
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {article.subtitle}
        </Typography>

      </Box>
      <Box
        data-testid={`partners-individual-article-${index}`}
        sx={{
          display: 'flex',
          padding: theme.spacing(2),
          justifyContent: 'flex-end',
        }}
      >
        <Typography
          variant='h6'
        >{cta}</Typography>
      </Box>
    </Box>
  );

  if (!articles || articles.length === 0) return null;

  return (
    <>
      <Box
        id='related-articles'
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: theme.spacing(2),
          alignSelf: 'stretch',
        }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(1),
          }}
        >
          <Typography variant='h4'>{t('partnerDetails.relatedArticles')}</Typography>
        </Box>
        <Grid container spacing={4}>
          {articles?.map((article, i) => {
            const link = article.isInternal ? `${baseUrl}${article.resourceLink}` : article.resourceLink;
            const cta = (() => {
              switch (article.resourceType) {
                case PartnerProfileResourceType.ARTICLE:
                  return t('content.feed.viewArticle');
                case PartnerProfileResourceType.VIDEO:
                  return t('content.feed.watchVideo');
                case PartnerProfileResourceType.PDF:
                case PartnerProfileResourceType.DOC:
                  return t('content.feed.downloadResource');
                default:
                  return t('content.feed.viewResource');
              }
            })();

            return (
              <Grid item xs={12} md={6} key={i}>
                <Card variant="outlined" sx={{
                  borderRadius: theme.spacing(1.5),
                  height: '100%',
                }}>
                  {(() => {
                    const trackingAction = (() => {
                      switch (article.resourceType) {
                        case PartnerProfileResourceType.ARTICLE:
                          return `Read${partnerKeyName}Article`;
                        case PartnerProfileResourceType.VIDEO:
                          return `Watch${partnerKeyName}Video`;
                        case PartnerProfileResourceType.PDF:
                        case PartnerProfileResourceType.DOC:
                          return `Preview${partnerKeyName}Resource`;
                        default:
                          return `View${partnerKeyName}Resource`;
                      }
                    })();

                    const trackingPayload = {
                      articleTitle: article.title,
                      articleDescription: article.subtitle,
                      articleLink: link,
                    };

                    if (article.resourceType === PartnerProfileResourceType.VIDEO) {
                      const handleVideoClick = () => {
                        trackPartnerDetail('Click', trackingAction, trackingPayload);
                        openVideoDialog(link, article.title);
                      };
                      return (
                        <Box
                          role="button"
                          tabIndex={0}
                          sx={{ cursor: 'pointer', textDecoration: 'none', color: 'inherit' }}
                          onClick={handleVideoClick}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleVideoClick();
                            }
                          }}
                        >
                          {renderCardContent(article, cta, i)}
                        </Box>
                      );
                    }

                    if (article.resourceType === PartnerProfileResourceType.PDF ||
                      article.resourceType === PartnerProfileResourceType.DOC) {
                      const handlePdfClick = () => {
                        trackPartnerDetail('Click', trackingAction, trackingPayload);
                        openPdfDialog(link, article.title);
                      };
                      return (
                        <Box
                          role="button"
                          tabIndex={0}
                          sx={{ cursor: 'pointer', textDecoration: 'none', color: 'inherit' }}
                          onClick={handlePdfClick}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handlePdfClick();
                            }
                          }}
                        >
                          {renderCardContent(article, cta, i)}
                        </Box>
                      );
                    }

                    return article.isInternal ? (
                      <Link
                        to={getInternalPath(link)}
                        style={{ textDecoration: 'none' }}
                        onClick={() => {
                          trackPartnerDetail('Click', trackingAction, trackingPayload);
                        }}
                      >
                        {renderCardContent(article, cta, i)}
                      </Link>
                    ) : (
                      <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'none' }}
                        onClick={(e) => {
                          // Already opened by the Service Nation link interceptor
                          const isHandled = e.defaultPrevented;
                          e.preventDefault();
                          trackPartnerDetail('Click', trackingAction, trackingPayload);
                          if (!isHandled) openExternalLink(link);
                        }}
                      >
                        {renderCardContent(article, cta, i)}
                      </a>
                    );
                  })()}
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box >

      <ResourcePreviewDialog
        videoDialog={videoDialog}
        pdfDialog={pdfDialog}
        onCloseVideo={closeVideoDialog}
        onClosePdf={closePdfDialog}
      />
    </>
  );
};
